import { useEffect, useRef, useState } from "react";
import { Link, useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import {
  SUPPLIER_CLAIM_STATUSES,
  SUPPLIER_RESPONSES,
  canAccess,
  type AccessVerb,
  claimEditedAfterResponse,
  claimTotalMinor,
  minorToAmount,
  roleAccess,
  supplierClaimDraftSchema,
  type SupplierClaimAction,
  type SupplierClaimDto,
  type SupplierClaimSourceDto,
  type SupplierClaimStatus,
  type SupplierResponse,
  type SupplierSuggestionDto,
} from "@hisense/shared";
import { useAuth } from "@/app/auth";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { PageActions, PageBody, PageHeader } from "@/components/ui/PageHeader";
import { FieldGrid, FormGroup, SPAN_CLASS } from "@/components/ui/FormLayout";
import { SettledRules } from "@/components/ui/SettledRules";
import { FieldLabel, Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button, buttonStyles } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { ExportButton } from "@/components/domain/ExportButton";
import { ClaimOptionPicker } from "@/components/claims/ClaimOptionPicker";
import { IssueClaimFields, type IssueClaimValue } from "@/components/claims/IssueClaimFields";
import { formatMoney } from "@/domain/money";
import { formatDateTime } from "@/domain/datetime";
import { ERP_PAGE_SIZE } from "@/lib/exportRows";
import { claimsEn } from "@/i18n/claims";

type Page = { data: SupplierClaimDto[]; hasMore: boolean; total: number; page: number };
const listPage = (q: string, status: string, page: number, signal?: AbortSignal, supplierId = "") =>
  apiFetch<Page>("/supplier-claims", { query: { q, status: status || undefined, supplierId: supplierId || undefined, page }, signal });

/**
 * Bốn quy tắc của Khiếu nại nhà cung cấp — chốt 20/09/2026 theo chuẩn (SCL-R01, SCL-R02 + nguồn, ai được làm gì), khách bác lại
 * được. Trước đây là hai thẻ vàng "chờ khách xác nhận"; nay là một mục gập trung tính.
 */
export const SUPPLIER_CLAIM_RULES_SETTLED = ["source", "response", "money", "authority"] as const;
function ClaimRules() {
  return <SettledRules ns="supplierClaims" rules={SUPPLIER_CLAIM_RULES_SETTLED} icon={ShieldAlert} />;
}

/** Cùng bộ giải quyền với máy chủ — theo ô tick của menu (trước đây nút theo quyền chung `records:*`, suy từ bất kỳ menu nào). */
function useClaimAccess() {
  const { user } = useAuth();
  const access = user ? (user.access ?? roleAccess(user.role)) : null;
  return (verb: AccessVerb, menu: "supplierClaims" | "finance" = "supplierClaims") => !!access && canAccess(access, menu, verb);
}

const money = (minor: number | null, currency: string | null) => (minor == null ? "—" : currency ? formatMoney(minor, currency) : minorToAmount(minor, 2));

export function SupplierClaimsPage() {
  const { t, i18n } = useTranslation();
  const can = useClaimAccess();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  // `?supplierId=`: nút "Xem tất cả" trên trang chi tiết nhà cung cấp mở thẳng danh sách đã lọc (18/09/2026).
  const [params, setParams] = useSearchParams();
  const supplierId = params.get("supplierId") ?? "";
  // Danh sách chọn đi qua đường của khiếu nại, không qua /suppliers — như ô chọn khi lập hồ sơ.
  const supplierOptions = useQuery({
    queryKey: ["supplier-claim-options", "suppliers", "", ""],
    queryFn: ({ signal }) => apiFetch<{ data: { id: string; label: string }[] }>("/supplier-claims/options/suppliers", { query: { q: "" }, signal }),
  });
  const setSupplier = (id: string) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set("supplierId", id);
      else next.delete("supplierId");
      return next;
    }, { replace: true });
    setPage(1);
  };
  // Thanh điều hướng đã có "Mua hàng › Khiếu nại nhà cung cấp"; thêm nữa là lặp.
  usePageCrumbs([]);
  const query = useQuery({ queryKey: ["supplier-claims", q, status, supplierId, page], queryFn: ({ signal }) => listPage(q, status, page, signal, supplierId) });
  const sourceText = (r: SupplierClaimDto) =>
    r.sourceKind === "CustomerCase" ? `${t("supplierClaims.sourceKinds.CustomerCase")} ${r.caseNo ?? ""}`.trim() : r.sourceKind === "Ncr" ? `NCR ${r.ncrNo ?? ""}` : t("supplierClaims.sourceKinds.Manual");
  return (
    <PageBody className="space-y-4">
      <PageHeader
        title={t("supplierClaims.title")}
        subtitle={t("supplierClaims.intro")}
        actions={can("create") ? <Link className={buttonStyles({ size: "sm" })} to="/supplier-claims/new">{t("supplierClaims.create")}</Link> : undefined}
      />
      <ClaimRules />
      <div className="flex flex-wrap items-end gap-3">
        <Input className="w-full max-w-sm" aria-label={t("supplierClaims.search")} placeholder={t("supplierClaims.search")} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <Select aria-label={t("supplierClaims.status")} containerClassName="w-48 min-h-11" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">{t("supplierClaims.allStatuses")}</option>
          {SUPPLIER_CLAIM_STATUSES.map((s) => (
            <option key={s} value={s}>{t(`supplierClaims.statuses.${s}`)}</option>
          ))}
        </Select>
        <Select aria-label={t("supplierClaims.supplier")} containerClassName="w-64 min-h-11" value={supplierId} onChange={(e) => setSupplier(e.target.value)}>
          <option value="">{t("supplierClaims.allSuppliers")}</option>
          {supplierId && !supplierOptions.data?.data.some((o) => o.id === supplierId) && (
            <option value={supplierId}>{query.data?.data[0]?.supplierName ?? supplierId}</option>
          )}
          {supplierOptions.data?.data.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </Select>
        <ExportButton<SupplierClaimDto>
          name="supplier-claims"
          title={t("supplierClaims.title")}
          rows={query.data?.data ?? []}
          total={query.data?.total}
          pageSize={ERP_PAGE_SIZE}
          disabled={query.isFetching}
          errorKey="supplierClaims.error"
          fetchPage={(p, _size, signal) => listPage(q, status, p, signal, supplierId)}
          columns={[
            { header: t("supplierClaims.claimNo"), value: (r) => r.claimNo },
            { header: t("supplierClaims.supplier"), value: (r) => r.supplierName },
            { header: t("supplierClaims.source"), value: (r) => sourceText(r) },
            { header: t("supplierClaims.product"), value: (r) => r.productCode },
            { header: t("supplierClaims.lot"), value: (r) => r.lotNumber },
            { header: t("supplierClaims.po"), value: (r) => r.poNo },
            { header: t("claims.issue"), value: (r) => t(`claims.issues.${r.issueCode}.label`) },
            { header: t("claims.severity"), value: (r) => t(`claims.severities.${r.severity}`) },
            { header: t("claims.claimsSupplier"), value: (r) => r.claims.map((c) => t(`claims.types.${c.type}`)).join("; ") },
            { header: t("supplierClaims.claimed"), value: (r) => (r.claimedMinor ? minorToAmount(r.claimedMinor, 2) : "") },
            { header: t("supplierClaims.agreed"), value: (r) => (r.agreedMinor == null ? "" : minorToAmount(r.agreedMinor, 2)) },
            { header: t("claims.currency"), value: (r) => r.currency },
            { header: t("supplierClaims.response"), value: (r) => (r.supplierResponse ? t(`supplierClaims.responses.${r.supplierResponse}`) : "") },
            { header: t("supplierClaims.status"), value: (r) => t(`supplierClaims.statuses.${r.status}`) },
            { header: t("common.createdOn"), value: (r) => formatDateTime(r.createdAt, i18n.language) },
          ]}
        />
      </div>
      {query.isLoading ? (
        <LoadingState />
      ) : query.error ? (
        <ErrorState message={t("supplierClaims.error")} onRetry={() => void query.refetch()} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line bg-surface">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead className="bg-sunken">
              <tr>
                {(["claimNo", "supplier", "claimed", "status"] as const).map((k, i) => (
                  <th key={k} className={i === 2 ? "p-3 text-right" : "p-3"}>
                    {i === 1 ? `${t("supplierClaims.supplier")} · ${t("claims.issue")}` : t(`supplierClaims.${k}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {query.data?.data.map((r) => (
                <tr key={r.id} className="border-t border-line align-top">
                  <td className="p-3">
                    <Link className="font-mono text-brand underline" to={`/supplier-claims/${r.id}`}>{r.claimNo}</Link>
                    <p className="text-xs text-ink-soft">{sourceText(r)}</p>
                  </td>
                  <td className="p-3">
                    {r.supplierName}
                    <p className="text-xs text-ink-soft">
                      {t(`claims.issues.${r.issueCode}.label`)} · {t(`claims.severities.${r.severity}`)}
                      {r.productCode && <span className="font-mono"> · {r.productCode}{r.lotNumber ? ` / ${r.lotNumber}` : ""}</span>}
                    </p>
                  </td>
                  <td className="p-3 text-right font-mono tabular">
                    {r.claimedMinor ? money(r.claimedMinor, r.currency) : "—"}
                    {r.agreedMinor != null && <p className="text-xs text-ink-soft">{t("supplierClaims.agreed")}: {money(r.agreedMinor, r.currency)}</p>}
                  </td>
                  <td className="p-3">
                    {t(`supplierClaims.statuses.${r.status}`)}
                    {r.supplierResponse && <p className="text-xs text-ink-soft">{t(`supplierClaims.responses.${r.supplierResponse}`)}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {query.data?.data.length === 0 && <p className="p-4 text-sm text-ink-soft">{t("supplierClaims.empty")}</p>}
        </div>
      )}
      <div className="flex justify-end gap-3">
        <Button variant="secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</Button>
        <span className="p-2 font-mono">{page}</span>
        <Button variant="secondary" disabled={!query.data?.hasMore} onClick={() => setPage((p) => p + 1)}>›</Button>
      </div>
    </PageBody>
  );
}

export function SupplierClaimEditorPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const caseId = params.get("caseId") ?? "";
  const ncrId = params.get("ncrId") ?? "";
  const claim = useQuery({ enabled: !!id, queryKey: ["supplier-claim", id], queryFn: ({ signal }) => apiFetch<{ data: SupplierClaimDto }>(`/supplier-claims/${id}`, { signal }) });
  const sourceParams = id ? { caseId: claim.data?.data.caseId ?? "", ncrId: claim.data?.data.ncrId ?? "" } : { caseId, ncrId };
  const wantsSource = !!(sourceParams.caseId || sourceParams.ncrId);
  const source = useQuery({
    enabled: wantsSource && (!id || !!claim.data),
    queryKey: ["supplier-claim-source", sourceParams.caseId, sourceParams.ncrId],
    queryFn: ({ signal }) => apiFetch<{ data: SupplierClaimSourceDto }>("/supplier-claims/source", { query: sourceParams, signal }),
  });
  if ((id && claim.isLoading) || (wantsSource && source.isLoading)) return <LoadingState />;
  if (id && (claim.error || !claim.data)) return <ErrorState message={t("supplierClaims.error")} onRetry={() => void claim.refetch()} />;
  if (!id && wantsSource && source.error) return <ErrorState message={t("supplierClaims.errors.source")} onRetry={() => void source.refetch()} />;
  return <ClaimForm key={id ?? `new:${caseId}:${ncrId}`} existing={claim.data?.data} source={source.data?.data} />;
}

const emptyValue = (source?: SupplierClaimSourceDto): IssueClaimValue => ({ issueCode: source?.issueCode ?? "", severity: source?.severity ?? null, claims: [], claimCurrency: "" });
const valueOf = (row: SupplierClaimDto): IssueClaimValue => ({
  issueCode: row.issueCode,
  severity: row.severity,
  claims: row.claims.map((c) => ({ type: c.type, amount: c.amountMinor == null ? "" : minorToAmount(c.amountMinor, 2) })),
  claimCurrency: row.currency ?? "",
});

function ClaimForm({ existing, source }: { existing?: SupplierClaimDto; source?: SupplierClaimSourceDto }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const can = useClaimAccess();
  const navigate = useNavigate();
  const client = useQueryClient();
  const lock = useRef(false);
  const allowLeave = useRef(false);
  const request = useRef(existing?.id ?? crypto.randomUUID());
  const [saved, setSaved] = useState(existing);
  const [supplier, setSupplier] = useState({ id: existing?.supplierId ?? "", label: existing?.supplierName ?? "" });
  const [product, setProduct] = useState({ id: existing?.productId ?? "", label: existing?.productCode ?? "" });
  const [po, setPo] = useState({ id: existing?.poId ?? "", label: existing?.poNo ?? "" });
  const [lotNumber, setLotNumber] = useState(existing?.lotNumber ?? "");
  const [cls, setCls] = useState<IssueClaimValue>(existing ? valueOf(existing) : emptyValue(source));
  const [description, setDescription] = useState(existing?.description ?? source?.description ?? "");
  const [reason, setReason] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<SupplierClaimAction["action"] | null>(null);
  usePageCrumbs([{ label: saved?.claimNo ?? t("supplierClaims.create") }]);

  const sourceKind = saved?.sourceKind ?? (source?.kind ?? "Manual");
  // Cùng luật với máy chủ (SCL-R01): duyệt cần ô Duyệt và không phải người lập hồ sơ — kể cả quản trị viên.
  const mine = !!saved && saved.createdBy === user?.id;
  const canApprove = can("approve") && !mine;
  const editableStatus = !saved || saved.status === "Draft" || saved.status === "Responded";
  // Khiếu nại của nhà cung cấp riêng tư mình không được thấy (28/10/2026): chỉ xem — máy chủ cũng chặn (`private_supplier`).
  const supplierHidden = saved?.supplierHidden === true;
  const canEdit = editableStatus && can(saved ? "update" : "create") && (saved ? true : !source || source.eligible) && !supplierHidden;
  const canAct = !!saved && can("update") && !supplierHidden;

  const blocker = useBlocker(() => !allowLeave.current && (dirty || busy));
  useEffect(() => {
    const fn = (e: BeforeUnloadEvent) => {
      if ((dirty || busy) && !allowLeave.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", fn);
    return () => window.removeEventListener("beforeunload", fn);
  }, [dirty, busy]);

  const touch = () => {
    setDirty(true);
    if (!saved) request.current = crypto.randomUUID();
  };
  const accept = (row: SupplierClaimDto) => {
    setSaved(row);
    setSupplier({ id: row.supplierId, label: row.supplierName });
    setProduct({ id: row.productId ?? "", label: row.productCode ?? "" });
    setPo({ id: row.poId ?? "", label: row.poNo ?? "" });
    setLotNumber(row.lotNumber ?? "");
    setCls(valueOf(row));
    setDescription(row.description);
    setReason("");
    setDirty(false);
    client.setQueryData(["supplier-claim", row.id], { data: row });
    void client.invalidateQueries({ queryKey: ["supplier-claims"] });
    if (row.caseId) void client.invalidateQueries({ queryKey: ["case-supplier-claims", row.caseId] });
  };
  const pickSuggestion = (s: SupplierSuggestionDto) => {
    setSupplier({ id: s.supplierId, label: s.supplierName });
    setProduct({ id: s.productId ?? "", label: s.productCode ?? "" });
    setLotNumber(s.lotNumber ?? "");
    setPo({ id: s.poId ?? "", label: s.poNo ?? "" });
    touch();
  };

  const save = async () => {
    if (lock.current || !canEdit) return;
    const body = {
      requestId: request.current,
      supplierId: supplier.id,
      sourceKind,
      caseId: sourceKind === "CustomerCase" ? (saved?.caseId ?? source?.id ?? null) : null,
      ncrId: sourceKind === "Ncr" ? (saved?.ncrId ?? source?.id ?? null) : null,
      productId: product.id || null,
      lotNumber: lotNumber.trim() || null,
      poId: po.id || null,
      issueCode: cls.issueCode,
      severity: cls.severity,
      claims: cls.claims.map((c) => ({ type: c.type, amount: c.amount.trim() || null })),
      currency: cls.claimCurrency || null,
      description,
    };
    const parsed = supplierClaimDraftSchema.safeParse(body);
    if (!parsed.success || (saved && reason.trim().length < 5)) {
      const code = parsed.success ? null : parsed.error.issues.map((i) => i.message).find((m) => m in claimsEn.errors);
      setError(code ? t(`claims.errors.${code}`) : t("supplierClaims.invalid"));
      return;
    }
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      const response = await apiFetch<{ data: SupplierClaimDto }>(saved ? `/supplier-claims/${saved.id}` : "/supplier-claims", {
        method: saved ? "PUT" : "POST",
        body: saved ? { ...parsed.data, requestId: saved.id, revision: saved.revision, reason } : parsed.data,
      });
      accept(response.data);
      if (!saved) {
        allowLeave.current = true;
        navigate(`/supplier-claims/${response.data.id}`, { replace: true });
      }
    } catch (e) {
      setError(getLocalizedError(e, t, "supplierClaims.error"));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  // Sửa sau khi nhà cung cấp phản hồi thì câu trả lời cũ không còn áp — gửi lại trước khi duyệt (máy chủ cũng chặn).
  const editedAfterResponse = !!saved && claimEditedAfterResponse(saved.status, saved.events);
  // Thứ tự nút: việc phụ trước, việc chính của trạng thái hiện tại sau cùng (sát tay phải). Người chỉ có ô Duyệt thấy mỗi nút Duyệt.
  // Đã phản hồi: gửi lại chỉ khi nhà cung cấp không đồng ý hoặc hồ sơ vừa sửa — chấp nhận rồi thì việc kế tiếp là duyệt.
  const actions: SupplierClaimAction["action"][] = [];
  if (saved) {
    if (canAct && ["Draft", "Sent", "Responded"].includes(saved.status)) actions.push("cancel");
    if (canAct && saved.status === "Draft") actions.push("send");
    if (canAct && saved.status === "Sent") actions.push("recordResponse");
    if (canAct && saved.status === "Responded" && (saved.supplierResponse === "Disputed" || editedAfterResponse)) actions.push("send");
    if (canApprove && (saved.status === "Sent" || (saved.status === "Responded" && !editedAfterResponse))) actions.push("approve");
    if (canAct && saved.status === "Approved") actions.push("close");
  }
  const main = actions.at(-1);
  const selfReview = !!saved && mine && can("approve") && ["Sent", "Responded"].includes(saved.status) && !editedAfterResponse;
  // Người không có ô Duyệt: hồ sơ đã có phản hồi thì nói rõ đang chờ ai, thay vì để thanh thao tác trống.
  const awaitingApproval = !!saved && saved.status === "Responded" && !editedAfterResponse && !can("approve");
  // Có tiền thì chỉ đóng được khi phiếu ghi nợ đã vào sổ (máy chủ và trigger cũng chặn); khoá nút và nói rõ vì sao.
  const closeBlocked = !!saved && (saved.agreedMinor ?? 0) > 0 && saved.debitNoteStatus !== "Posted";
  const canOpenFinance = can("view", "finance");
  const suggestions = source?.suggestions ?? [];

  return (
    <PageBody className="space-y-4">
      <ConfirmDialog
        open={blocker.state === "blocked"}
        title={t("common.unsavedTitle")}
        description={t("common.unsavedDescription")}
        confirmLabel={t("common.discardChanges")}
        cancelLabel={t("common.keepEditing")}
        busy={busy}
        onCancel={() => blocker.reset?.()}
        onConfirm={() => { allowLeave.current = true; blocker.proceed?.(); }}
      />
      <PageHeader title={saved?.claimNo ?? t("supplierClaims.create")} subtitle={saved ? saved.supplierName : t("supplierClaims.intro")} actions={<PageActions><Link className="text-sm text-brand underline" to="/supplier-claims">{t("supplierClaims.back")}</Link></PageActions>} />
      <ClaimRules />
      {(saved?.caseNo || saved?.ncrNo || source) && (
        <p className="text-sm">
          {sourceKind === "CustomerCase"
            ? t("supplierClaims.fromCase", { no: saved?.caseNo ?? source?.no ?? "" })
            : t("supplierClaims.fromNcr", { no: saved?.ncrNo ?? source?.no ?? "" })}
        </p>
      )}
      {source && !source.eligible && !saved && <p role="alert" className="rounded-sm bg-warn-tint px-3 py-2 text-sm text-warn">{t("supplierClaims.needsSupplierCause")}</p>}
      {saved && (editableStatus || supplierHidden) && !canEdit && <p className="rounded-sm bg-sunken px-3 py-2 text-xs text-ink-soft">{supplierHidden ? t("common.internalSupplierHint") : t("errors.readOnly")}</p>}
      {/* ── Gợi ý nhà cung cấp kèm căn cứ — chỉ khi lập từ khiếu nại khách / NCR. ── */}
      {canEdit && source && (
        <FormGroup id="scl-group-suggest" title={t("supplierClaims.suggestions")} hint={t("supplierClaims.suggestionsHint")}>
          {suggestions.length === 0 ? (
            <p className="text-sm text-ink-soft">{t("supplierClaims.noSuggestions")}</p>
          ) : (
            <ul className="flex flex-wrap gap-2 text-sm">
              {suggestions.map((s, i) => (
                <li key={i}>
                  <button type="button" className="rounded-sm border border-line px-3 py-2 text-left hover:border-brand" onClick={() => pickSuggestion(s)}>
                    <span className="font-medium">{s.supplierName}</span>
                    <span className="block text-xs text-ink-soft">
                      {t(`supplierClaims.basis.${s.basis}`)}
                      {s.productCode && <span className="font-mono"> · {s.productCode}</span>}
                      {s.lotNumber && <span className="font-mono"> / {s.lotNumber}</span>}
                      {s.poNo && <span className="font-mono"> · {s.poNo}</span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </FormGroup>
      )}
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <fieldset disabled={busy || !canEdit} className="min-w-0 space-y-4">
          {/* ── Nhà cung cấp và hàng lỗi — ai chịu, hàng nào, lô nào, đơn mua nào. ── */}
          <FormGroup id="scl-group-supplier" title={t("supplierClaims.groups.supplier")} hint={t("supplierClaims.groups.supplierHint")}>
            <FieldGrid>
              <div className={SPAN_CLASS[6]}><ClaimOptionPicker kind="suppliers" value={supplier.id} selectedLabel={supplier.label} label={t("supplierClaims.supplier")} disabled={!canEdit || busy} onChange={(id, label) => { setSupplier({ id, label }); setPo({ id: "", label: "" }); touch(); }} /></div>
              <div className={SPAN_CLASS[6]}><ClaimOptionPicker kind="purchaseOrders" value={po.id} selectedLabel={po.label} supplierId={supplier.id} allowEmpty label={t("supplierClaims.po")} disabled={!canEdit || busy || !supplier.id} onChange={(id, label) => { setPo({ id, label }); touch(); }} /></div>
              <div className={SPAN_CLASS[6]}><ClaimOptionPicker kind="products" value={product.id} selectedLabel={product.label} allowEmpty label={t("supplierClaims.product")} disabled={!canEdit || busy} onChange={(id, label) => { setProduct({ id, label }); touch(); }} /></div>
              <div className={SPAN_CLASS[6]}>
                <FieldLabel htmlFor="scl-lot">{t("supplierClaims.lot")}</FieldLabel>
                <Input id="scl-lot" className="font-mono" value={lotNumber} maxLength={100} onChange={(e) => { setLotNumber(e.target.value); touch(); }} />
              </div>
            </FieldGrid>
          </FormGroup>
          {/* ── Khiếu nại — loại lỗi, mức độ, đòi gì và số tiền; sự việc nhà cung cấp đọc được trên cổng. ── */}
          <FormGroup id="scl-group-claim" title={t("supplierClaims.groups.claim")} hint={t("supplierClaims.groups.claimHint")}>
            <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2 [&>div]:min-w-0">
              <IssueClaimFields side="supplier" idPrefix="scl" value={cls} disabled={!canEdit || busy} onChange={(v) => { setCls(v); touch(); }} />
              <div className="md:col-span-2">
                <FieldLabel htmlFor="scl-description">{t("supplierClaims.description")}</FieldLabel>
                {canEdit ? (
                  <>
                    <textarea id="scl-description" aria-describedby="scl-description-hint" className="min-h-24 w-full resize-y rounded-sm border border-line-strong bg-surface p-3 text-sm outline-brand" value={description} maxLength={2000} onChange={(e) => { setDescription(e.target.value); touch(); }} />
                    {/* Mô tả đi thẳng lên Cổng nhà cung cấp. Lập từ khiếu nại khách thì ô này để TRỐNG (YC-01, 07/10/2026): chữ của hồ sơ
                        khách hay mang tên khách, giá bán — hiện riêng bên dưới, chỉ đọc, để nhân viên tự viết lại cho nhà cung cấp. */}
                    <p id="scl-description-hint" className="mt-1 text-xs text-ink-soft">{t("supplierClaims.descriptionHint")}</p>
                    {source?.caseText && (
                      <div className="mt-2 rounded-sm border border-line bg-sunken px-3 py-2 text-xs">
                        <p className="font-medium text-ink">{t("supplierClaims.caseText")}</p>
                        <p className="mt-1 whitespace-pre-wrap break-words text-ink-soft">{source.caseText}</p>
                        <p className="mt-1 text-ink-soft">{t("supplierClaims.caseTextHint")}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <p id="scl-description" className="whitespace-pre-wrap break-words text-sm">{description}</p>
                )}
              </div>
              {saved && canEdit && (
                <div className="md:col-span-2">
                  <FieldLabel htmlFor="scl-reason">{t("supplierClaims.editReason")}</FieldLabel>
                  <Input id="scl-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                </div>
              )}
            </div>
          </FormGroup>
        </fieldset>
      </form>
      {saved && (
        <>
          {/* ── Phản hồi và duyệt — nhà cung cấp trả lời gì, số tiền duyệt, phiếu ghi nợ. ── */}
          {(saved.supplierResponse || saved.agreedMinor != null) && (
            <FormGroup id="scl-group-decision" title={t("supplierClaims.groups.decision")} hint={t("supplierClaims.groups.decisionHint")}>
              <div className="grid gap-4 text-sm md:grid-cols-2">
                {saved.supplierResponse && (
                  <div>
                    <p className="font-semibold">{t("supplierClaims.response")}: {t(`supplierClaims.responses.${saved.supplierResponse}`)}</p>
                    {saved.supplierResponseNote && <p className="mt-1 whitespace-pre-wrap">{saved.supplierResponseNote}</p>}
                    <p className="mt-1 text-xs text-ink-soft">{saved.respondedByName ? `${t("supplierClaims.respondedBy")}: ${saved.respondedByName} · ` : ""}{formatDateTime(saved.respondedAt, i18n.language)}</p>
                  </div>
                )}
                {saved.agreedMinor != null && (
                  <div id="scl-agreed-block">
                    <p className="font-semibold">{t("supplierClaims.agreed")}: <span className="font-mono tabular">{money(saved.agreedMinor, saved.currency)}</span></p>
                    <p className="mt-1 text-xs text-ink-soft">{t("supplierClaims.approvedBy")}: {saved.approvedByName ?? "—"} · {formatDateTime(saved.approvedAt, i18n.language)}</p>
                    {saved.debitNoteNo && saved.debitNoteStatus && (
                      <div className="mt-3 border-t border-line pt-3">
                        <p>
                          {t("supplierClaims.debitNote")}: <span className="font-mono">{saved.debitNoteNo}</span> · {t(`supplierClaims.debitNoteStatuses.${saved.debitNoteStatus}`)}
                        </p>
                        <p className="mt-1 text-xs text-ink-soft">{t("supplierClaims.debitNoteHint")}</p>
                        {/* Lọc theo SỐ KHIẾU NẠI chứ không theo số phiếu: phiếu đã đảo và phiếu thay thế cùng hiện. */}
                        {canOpenFinance && (
                          <Link className="mt-1 inline-block text-xs text-brand underline" to={`/finance?tab=debitNotes&q=${encodeURIComponent(saved.claimNo)}`}>
                            {t("supplierClaims.openInFinance")}
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </FormGroup>
          )}
          <FormGroup id="scl-group-history" title={t("supplierClaims.history")}>
            <ol className="space-y-1.5 text-sm">
              {saved.events.map((e, i) => (
                <li key={i} className="flex flex-wrap gap-x-2">
                  <span className="font-mono tabular text-xs text-ink-faint">{formatDateTime(e.createdAt, i18n.language)}</span>
                  <span className="font-medium text-ink">{t(`supplierClaims.events.${e.action}`, { defaultValue: e.action })}</span>
                  {e.createdByName && <span className="text-ink-soft">· {e.createdByName}</span>}
                  {e.reason && <span className="whitespace-pre-wrap text-ink-soft">· {e.reason}</span>}
                </li>
              ))}
            </ol>
          </FormGroup>
        </>
      )}
      {/* Một thanh thao tác duy nhất, cuối trang và dính đáy. */}
      {error && !action && <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">{error}</p>}
      {selfReview && <p className="rounded-sm bg-sunken px-3 py-2 text-xs text-ink-soft">{t("supplierClaims.selfApproval")}</p>}
      {editedAfterResponse && <p className="rounded-sm bg-warn-tint px-3 py-2 text-xs text-warn">{t("supplierClaims.resendNeeded")}</p>}
      {awaitingApproval && <p className="rounded-sm bg-sunken px-3 py-2 text-xs text-ink-soft">{t("supplierClaims.awaitingApproval")}</p>}
      {closeBlocked && actions.includes("close") && <p className="rounded-sm bg-sunken px-3 py-2 text-xs text-ink-soft">{t("supplierClaims.errors.debitNote")}</p>}
      <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-end gap-x-3 gap-y-2 rounded-md border border-line bg-surface px-4 py-3 shadow-[var(--shadow-card)]">
        <p className="mr-auto text-sm">
          {saved && (
            <>
              {t(`supplierClaims.statuses.${saved.status}`)}
              {saved.claimedMinor > 0 && <span className="font-mono tabular"> · {t("supplierClaims.claimed")} {money(saved.claimedMinor, saved.currency)}</span>}
            </>
          )}
        </p>
        {canEdit && (
          <Button type="button" size="sm" disabled={busy || !dirty || !supplier.id || !cls.issueCode || description.trim().length < 5 || (!!saved && reason.trim().length < 5)} onClick={() => void save()}>
            {t("common.save")}
          </Button>
        )}
        {actions.map((a) => (
          <Button type="button" size="sm" key={a} variant={a === main && a !== "cancel" && !dirty ? "primary" : "secondary"} disabled={busy || dirty || (a === "close" && closeBlocked)} onClick={() => { setError(null); setAction(a); }}>
            {t(`supplierClaims.actions.${a}`)}
          </Button>
        ))}
      </div>
      {action && saved && <ClaimActionDialog key={action} row={saved} action={action} onClose={() => setAction(null)} onSaved={(r) => { accept(r); setAction(null); }} />}
    </PageBody>
  );
}

function ClaimActionDialog({ row, action, onClose, onSaved }: { row: SupplierClaimDto; action: SupplierClaimAction["action"]; onClose: () => void; onSaved: (row: SupplierClaimDto) => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [response, setResponse] = useState<SupplierResponse>("Accepted");
  const [responseNote, setResponseNote] = useState("");
  const [agreed, setAgreed] = useState(row.claimedMinor > 0 ? minorToAmount(row.claimedMinor, 2) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lock = useRef(false);
  const total = claimTotalMinor(row.claims);
  const save = async () => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await apiFetch<{ data: SupplierClaimDto }>(`/supplier-claims/${row.id}/actions`, {
        method: "POST",
        body: {
          revision: row.revision,
          action,
          reason,
          response: action === "recordResponse" ? response : null,
          responseNote: action === "recordResponse" ? responseNote : "",
          agreedAmount: action === "approve" && total > 0 ? agreed : null,
        },
      });
      onSaved(result.data);
    } catch (e) {
      setError(getLocalizedError(e, t, "supplierClaims.error"));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const invalid =
    reason.trim().length < 5 ||
    (action === "recordResponse" && response === "Disputed" && responseNote.trim().length < 5) ||
    (action === "approve" && total > 0 && !agreed.trim());
  return (
    <Modal title={t(`supplierClaims.actions.${action}`)} eyebrow={row.claimNo} closeLabel={t("common.close")} onClose={() => { if (!busy) onClose(); }} className="max-w-xl">
      <div className="min-h-0 space-y-4 overflow-y-auto p-4">
        <p className="text-sm">{t("supplierClaims.actionHint")}</p>
        {action === "recordResponse" && (
          <>
            <div>
              <FieldLabel htmlFor="scl-response">{t("supplierClaims.response")}</FieldLabel>
              <Select id="scl-response" containerClassName="w-full min-h-11" value={response} disabled={busy} onChange={(e) => setResponse(e.target.value as SupplierResponse)}>
                {SUPPLIER_RESPONSES.map((r) => (
                  <option key={r} value={r}>{t(`supplierClaims.responses.${r}`)}</option>
                ))}
              </Select>
            </div>
            <div>
              <FieldLabel htmlFor="scl-response-note">{t("supplierClaims.responseNote")}</FieldLabel>
              <textarea id="scl-response-note" className="min-h-20 w-full resize-y rounded-sm border border-line-strong bg-surface p-3 text-sm outline-brand" value={responseNote} disabled={busy} onChange={(e) => setResponseNote(e.target.value)} />
            </div>
          </>
        )}
        {action === "approve" &&
          (total > 0 ? (
            <div>
              <FieldLabel htmlFor="scl-agreed">{t("supplierClaims.agreed")} ({row.currency})</FieldLabel>
              <Input id="scl-agreed" className="min-h-11 max-w-48 font-mono tabular" inputMode="decimal" value={agreed} disabled={busy} onChange={(e) => setAgreed(e.target.value)} />
              <p className="mt-1 text-xs text-ink-soft">{t("supplierClaims.approveHint")} {t("supplierClaims.claimed")}: {money(total, row.currency)}</p>
            </div>
          ) : (
            <p className="text-sm text-ink-soft">{t("supplierClaims.approveNoMoney")}</p>
          ))}
        <div>
          <FieldLabel htmlFor="scl-action-reason">{t("supplierClaims.reason")}</FieldLabel>
          <Input id="scl-action-reason" className="min-h-11" value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} />
        </div>
        {error && <p role="alert" className="text-danger">{error}</p>}
      </div>
      <div className="flex shrink-0 justify-end gap-2 border-t border-line p-4">
        <Button variant="secondary" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button>
        <Button disabled={busy || invalid} onClick={() => void save()}>{t("crm.confirm")}</Button>
      </div>
    </Modal>
  );
}

/** Khối "Khiếu nại nhà cung cấp" trên trang hậu mãi. Không có quyền menu thì im lặng ẩn. */
export function CaseSupplierClaims({ caseId, rootCause, dirty, cancelled }: { caseId: string; rootCause: string; dirty: boolean; cancelled: boolean }) {
  const { t } = useTranslation();
  const can = useClaimAccess();
  const query = useQuery({
    queryKey: ["case-supplier-claims", caseId],
    retry: false,
    queryFn: ({ signal }) => apiFetch<Page>("/supplier-claims", { query: { caseId }, signal }),
  });
  if (query.error) return null;
  const rows = query.data?.data ?? [];
  // Nhóm như các nhóm khác của trang hậu mãi; nút lập chỉ hiện với người có ô Tạo ở menu Khiếu nại nhà cung cấp.
  const createLink = rootCause === "Supplier" && !cancelled && !dirty && can("create");
  return (
    <FormGroup id="tk-group-supplier-claims" title={t("supplierClaims.linked")} actions={createLink ? <Link className={buttonStyles({ variant: "secondary", size: "sm" })} to={`/supplier-claims/new?caseId=${caseId}`}>{t("supplierClaims.createFromCase")}</Link> : undefined}>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-soft">{t("supplierClaims.noLinked")}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {rows.map((r) => (
            <li key={r.id}>
              <Link className="font-mono text-brand underline" to={`/supplier-claims/${r.id}`}>{r.claimNo}</Link> · {r.supplierName} · {t(`supplierClaims.statuses.${r.status}` as `supplierClaims.statuses.${SupplierClaimStatus}`)}
              {r.claimedMinor > 0 && <span className="font-mono tabular"> · {money(r.agreedMinor ?? r.claimedMinor, r.currency)}</span>}
            </li>
          ))}
        </ul>
      )}
      {(rootCause !== "Supplier" || cancelled) && <p className="mt-2 text-xs text-ink-soft">{t("supplierClaims.needsSupplierCause")}</p>}
    </FormGroup>
  );
}
